document.documentElement.classList.add('de-js');

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const markFailedEditorialImage = (image) => {
  if (!(image instanceof HTMLImageElement) || !image.closest('.de-media')) return;
  image.hidden = true;
  image.setAttribute('data-de-image-failed', '');
  image.closest('.de-media')?.setAttribute('data-de-media-failed', '');
};

const restoreEditorialImage = (image) => {
  if (!(image instanceof HTMLImageElement) || image.naturalWidth === 0) return;
  const media = image.closest('.de-media');
  image.hidden = false;
  image.removeAttribute('data-de-image-failed');
  if (media && !media.querySelector('img[data-de-image-failed]')) media.removeAttribute('data-de-media-failed');
};

document.addEventListener('error', (event) => {
  if (event.target instanceof HTMLImageElement) markFailedEditorialImage(event.target);
}, true);

document.addEventListener('load', (event) => {
  if (event.target instanceof HTMLImageElement) restoreEditorialImage(event.target);
}, true);

const reconcileEditorialImages = () => {
  document.querySelectorAll('.de-media img').forEach((image) => {
    if (image.complete && image.naturalWidth === 0) markFailedEditorialImage(image);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', reconcileEditorialImages, { once: true });
} else {
  reconcileEditorialImages();
}

class DeReveal extends HTMLElement {
  connectedCallback() {
    const targets = this.matches('[data-de-reveal]') ? [this] : [...this.querySelectorAll('[data-de-reveal]')];
    if (!targets.length || reduceMotion() || !('IntersectionObserver' in window)) {
      targets.forEach((target) => target.setAttribute('data-de-revealed', ''));
      return;
    }

    this.observer = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.setAttribute('data-de-revealed', '');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    targets.forEach((target) => this.observer.observe(target));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

class DeFieldHero extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.handleMotion = () => {
      if (this.motionQuery.matches) {
        this.video.pause();
        this.video.removeAttribute('autoplay');
        return;
      }
      this.startObserver();
    };

    this.motionQuery.addEventListener?.('change', this.handleMotion);
    this.handleMotion();
  }

  startObserver() {
    if (!('IntersectionObserver' in window)) return;
    this.videoObserver?.disconnect();
    this.videoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !document.hidden && !reduceMotion()) {
          this.video.play().catch(() => {});
        } else {
          this.video.pause();
        }
      });
    }, { threshold: 0.2 });
    this.videoObserver.observe(this);
  }

  disconnectedCallback() {
    this.video?.pause();
    this.videoObserver?.disconnect();
    this.motionQuery?.removeEventListener?.('change', this.handleMotion);
  }
}

class DeRouteNavigator extends HTMLElement {
  connectedCallback() {
    this.buttons = [...this.querySelectorAll('[data-de-route-trigger], [data-de-route-button]')];
    this.panels = [...this.querySelectorAll('[data-de-route-panel]')];
    if (!this.buttons.length || !this.panels.length) return;

    this.buttons.forEach((button) => {
      button.addEventListener('mouseenter', () => this.activate(button.dataset.deRouteTrigger || button.dataset.deRouteButton));
      button.addEventListener('focus', () => this.activate(button.dataset.deRouteTrigger || button.dataset.deRouteButton));
    });
    this.activate(this.buttons[0].dataset.deRouteTrigger || this.buttons[0].dataset.deRouteButton);
  }

  activate(id) {
    this.buttons.forEach((button) => {
      const active = (button.dataset.deRouteTrigger || button.dataset.deRouteButton) === id;
      button.toggleAttribute('data-active', active);
    });
    this.panels.forEach((panel) => {
      const active = panel.dataset.deRoutePanel === id;
      panel.hidden = !active;
      panel.setAttribute('aria-hidden', String(!active));
    });
  }
}

if (!customElements.get('de-reveal')) customElements.define('de-reveal', DeReveal);
if (!customElements.get('de-field-hero')) customElements.define('de-field-hero', DeFieldHero);
if (!customElements.get('de-route-navigator')) customElements.define('de-route-navigator', DeRouteNavigator);
